/* global Element */

import PropTypes from 'prop-types'
import React, { forwardRef, useCallback } from 'react'
import DropdownListGroupItem from './DropdownListGroupItem'
import DropdownListItem from './DropdownListItem'
import { stickGroupLabelsToTop } from './utils'

/**
 * Create a React List component
 *
 * @template T
 * @template Item
 * @param {import('./interfaces').ListProps<T, Item>} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
export function list (
  {
    children,
    data,
    dropdownElementRef,
    dropdownListId,
    focusedItemIndex,
    getGroupChildren,
    isItemDisabled,
    itemClassName,
    itemProps,
    onGroupLabelClick,
    onItemClick,
    onMouseOut,
    onMouseOver,
    renderGroupLabel,
    renderItem,
    withGrouping,
    ...props
  },
  ref
) {
  /** @type {import('react').ReactEventHandler} */
  const handleListScroll = useCallback(
    ({ currentTarget }) => stickGroupLabelsToTop(currentTarget),
    []
  )

  return (
    <ul
      className='zb-dropdown-list zb-has-scrollbar'
      role='listbox'
      aria-label='Choose an item'
      ref={ref}
      onScroll={withGrouping ? handleListScroll : null}
      id={`${dropdownListId}-list`}
      {...props}
    >
      {withGrouping ? renderGroups() : data.map(renderListItem)}
      {children}
    </ul>
  )

  /**
   * @param {Item} d
   * @param {number} i
   */
  function renderListItem (d, i) {
    return (
      <DropdownListItem
        className={itemClassName(d, i)}
        data={d}
        id={`${dropdownListId}-${i}`}
        index={i}
        isDisabled={isItemDisabled(i)}
        isFocused={focusedItemIndex === i}
        itemProps={itemProps(d, i)}
        key={i}
        listElementRef={ref}
        onClick={onItemClick}
        onMouseOut={onMouseOut}
        onMouseOver={onMouseOver}
      >
        {renderItem(d, i)}
      </DropdownListItem>
    )
  }

  function renderGroups () {
    let offset = 0
    /** @type {JSX.Element[]} */
    const groups = []
    const nestedData = data.map(getGroupChildren)

    for (let groupIndex = 0; groupIndex < nestedData.length; groupIndex++) {
      const children = nestedData[groupIndex]
      const group = data[groupIndex]
      groups.push(
        <DropdownListGroupItem
          dropdownElementRef={dropdownElementRef}
          group={group}
          groupIndex={groupIndex}
          id={`${dropdownListId}-group-label-${groupIndex}`}
          key={groupIndex}
          label={renderGroupLabel(group, groupIndex)}
          onClick={onGroupLabelClick}
        >
          {children.map((child, childIndex) => {
            return renderListItem(child, offset + childIndex)
          })}
        </DropdownListGroupItem>
      )
      offset += children.length
    }
    return groups
  }
}

function noop () {}

/**
 * @template T
 * @template Item
 * @type {import('react').ForwardRefExoticComponent<import('./interfaces').ListProps<T, Item> & React.RefAttributes<HTMLElement>>}
 */
const List = forwardRef(list)

export default List

List.displayName = 'List'

List.defaultProps = {
  onItemClick: noop,
  onMouseOut: noop,
  onGroupLabelClick: noop
}

List.propTypes = {
  data: PropTypes.array.isRequired,
  dropdownElementRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),
  dropdownListId: PropTypes.string.isRequired,
  focusedItemIndex: PropTypes.number.isRequired,
  getGroupChildren: PropTypes.func,
  isItemDisabled: PropTypes.func.isRequired,
  itemClassName: PropTypes.func.isRequired,
  itemProps: PropTypes.func.isRequired,
  onGroupLabelClick: PropTypes.func,
  onItemClick: PropTypes.func,
  renderGroupLabel: PropTypes.func,
  renderItem: PropTypes.func.isRequired,
  onMouseOut: PropTypes.func,
  onMouseOver: PropTypes.func,
  withGrouping: PropTypes.bool
}
